<template>
  <div class="flex items-center min-w-0" :class="gapClass">
    <div class="relative shrink-0" :style="{ width: svgSize + 'px', height: svgSize + 'px' }">
      <svg
        :width="svgSize"
        :height="svgSize"
        class="absolute top-0 left-0 -rotate-90"
        aria-hidden="true"
      >
        <circle
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="NEUTRAL_RING_COLOR"
          :stroke-width="STROKE_WIDTH"
          fill="none"
        />
        <circle
          v-if="clampedScore > 0"
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="arcColor"
          :stroke-width="STROKE_WIDTH"
          fill="none"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="offset"
          stroke-linecap="round"
        />
      </svg>

      <ProtectedAvatar
        :src="resolvedAvatarUrl"
        :size="avatarSize"
        class="absolute"
        :style="{ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' }"
      >
        <div
          class="w-full h-full rounded-full bg-primary flex items-center justify-center text-white font-bold"
          :class="initialsTextClass"
        >
          {{ displayInitials }}
        </div>
      </ProtectedAvatar>
    </div>

    <div class="flex flex-col text-left min-w-0">
      <span class="leading-tight" :class="[nameClass, { truncate: props.truncate }]">
        {{ displayName }}
      </span>
      <span
        v-if="subtitle"
        class="font-bold mt-0.5"
        :class="[subtitleClass, { truncate: props.truncate }]"
      >
        {{ subtitle }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { getDoctorInitials, capitalizeDoctorName, capitalizeFullName } from '@/utils/string'
import { resolveAvatarSrc } from '@/utils/avatar'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'

type Size = 'sm' | 'md' | 'lg' | 'xl'

const STROKE_WIDTH = 4

const AVATAR_PX: Record<Size, number> = {
  sm: 40,
  md: 44,
  lg: 48,
  xl: 56
}

const props = withDefaults(
  defineProps<{
    name: string | null
    subtitle?: string | null
    avatarUrl?: string | null
    size?: Size
    placeholder?: string
    npsScore?: number | null
    /** Trunca nome/CRM com "..." quando o container não tem espaço (ex.: colunas de tabela). */
    truncate?: boolean
    /** Exibe o nome completo em vez de nome + primeiro sobrenome (ex.: nome de unidade). */
    fullName?: boolean
  }>(),
  {
    subtitle: null,
    avatarUrl: null,
    size: 'md',
    placeholder: '—',
    npsScore: null,
    truncate: false,
    fullName: false
  }
)

const resolvedAvatarUrl = computed(() => resolveAvatarSrc(props.avatarUrl))

const avatarSize = computed(() => AVATAR_PX[props.size] ?? AVATAR_PX.md)
const radius = computed(() => avatarSize.value / 2 + STROKE_WIDTH / 2)
const svgSize = computed(() => radius.value * 2 + STROKE_WIDTH)
const circumference = computed(() => 2 * Math.PI * radius.value)

const clampedScore = computed(() => {
  if (props.npsScore == null) return 0
  // NPS range is -100..100; normalize to 0..100 for ring fill
  return Math.min(100, Math.max(0, (props.npsScore + 100) / 2))
})

const offset = computed(() => circumference.value * (1 - clampedScore.value / 100))

const arcColor = computed(() => {
  if (props.npsScore == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(props.npsScore).color
})

const initialsTextClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'text-[12px]'
    case 'md':
      return 'text-[13px]'
    case 'lg':
    case 'xl':
      return 'text-[14px]'
    default:
      return 'text-[13px]'
  }
})

const gapClass = computed(() => {
  return props.size === 'lg' || props.size === 'xl' ? 'gap-4' : 'gap-3'
})

const nameClass = computed(() => {
  const hasName = !!props.name
  const color = hasName ? 'text-gray-900' : 'text-gray-400'

  switch (props.size) {
    case 'sm':
      return `font-normal text-[15px] ${color}`
    case 'md':
      return `font-medium text-base ${color}`
    case 'lg':
    case 'xl':
      return `font-medium text-lg ${color}`
    default:
      return `font-medium text-base ${color}`
  }
})

const subtitleClass = computed(() => {
  const hasName = !!props.name
  const color = hasName ? 'text-gray-900' : 'text-gray-400'

  switch (props.size) {
    case 'sm':
      return `text-[13px] ${color}`
    case 'md':
      return `text-xs ${color}`
    case 'lg':
    case 'xl':
      return `text-sm ${color}`
    default:
      return `text-xs ${color}`
  }
})

const displayInitials = computed(() => {
  return props.name ? getDoctorInitials(props.name) : props.placeholder
})

const displayName = computed(() => {
  if (!props.name) return props.placeholder
  return props.fullName ? capitalizeFullName(props.name) : capitalizeDoctorName(props.name)
})
</script>
